import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "./ui/card";
import { Sun, Sunrise, Sunset, Moon, Sparkles, ClipboardList, AlertTriangle, Calendar, X } from "lucide-react";

const GREETING_NO = {
  morning: "God morgen",
  day: "God dag",
  evening: "God kveld",
  night: "God natt",
};
const GREETING_EN = {
  morning: "Good morning",
  day: "Good day",
  evening: "Good evening",
  night: "Good night",
};

const GREETING_ICON = {
  morning: Sunrise,
  day: Sun,
  evening: Sunset,
  night: Moon,
};

const KIND_ICON = {
  cleaning: Sparkles,
  checklist: ClipboardList,
  defects: AlertTriangle,
  booking: Calendar,
};

/**
 * WelcomeBanner – time-of-day greeting + short, role-specific to-do briefing.
 *
 * Shows up at every login and any time the briefing content changes. The
 * dismissal flag is keyed on the user's last_login_at + a hash of the
 * current briefing, so a new login or new items will re-surface the banner.
 */
function _hashBriefing(data) {
  if (!data) return "";
  const parts = (data.briefing || []).map((b) => `${b.kind}:${b.text}`);
  // Cheap, deterministic short hash (DJB2)
  let h = 5381;
  const s = `${data.greeting_key}|${parts.join("|")}`;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36).slice(0, 8);
}

export default function WelcomeBanner({ compact = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  // The dismiss key combines user + last_login_at + briefing hash. A new login
  // or new briefing items mean the user has never dismissed THIS state, so the
  // banner shows again.
  const dismissKey = user && data
    ? `cbt:welcome-dismissed:${user.id || user.email}:${user.last_login_at || ""}:${_hashBriefing(data)}`
    : null;
  const [dismissed, setDismissed] = useState(false);

  const lang = user?.language === "en" ? "en" : "no";
  const dictGreet = lang === "en" ? GREETING_EN : GREETING_NO;

  useEffect(() => {
    if (!user) return;
    api
      .get("/dashboard/welcome")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [user]);

  // Re-evaluate dismissal whenever the computed key changes.
  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(sessionStorage.getItem(dismissKey) === "1");
  }, [dismissKey]);

  if (!user || dismissed || !data) return null;

  const onClose = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  const GIcon = GREETING_ICON[data.greeting_key] || Sun;
  const greeting = dictGreet[data.greeting_key] || dictGreet.day;
  const briefing = data.briefing || [];

  return (
    <Card
      data-testid="welcome-banner"
      className={`relative overflow-hidden border-2 border-[#BD984D]/40 bg-gradient-to-br from-[#0C1824] via-[#11283A] to-[#1DA3B3]/30 text-white ${
        compact ? "p-4" : "p-5 sm:p-6"
      }`}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Lukk velkomstmelding"
        data-testid="welcome-banner-dismiss"
        className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#BD984D]/20 flex items-center justify-center">
          <GIcon className="w-6 h-6 text-[#ECC974]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
            {data.is_super_admin ? (lang === "en" ? "Super-admin" : "Hovedadmin") : (lang === "en" ? data.role : roleLabelNo(data.role))}
          </div>
          <div
            className="text-xl sm:text-2xl font-bold mt-1"
            data-testid="welcome-banner-greeting"
          >
            {greeting}
            {data.name ? `, ${data.name.split(" ")[0]}` : ""}.
          </div>

          {data.role === "kunde" && (
            <p className="text-sm text-white/80 mt-2 max-w-xl">
              {lang === "en"
                ? "Welcome back. Your booking details are below."
                : "Velkommen tilbake. Se bookinginformasjon under."}
            </p>
          )}

          {data.role !== "kunde" && briefing.length === 0 && (
            <p className="text-sm text-white/80 mt-2">
              ✓ Ingenting akutt akkurat nå – alt ser bra ut.
            </p>
          )}

          {data.role !== "kunde" && briefing.length > 0 && (
            <>
              <p className="text-sm text-white/80 mt-2">
                Det er noen ting verdt å se på:
              </p>
              <ul className="mt-3 space-y-1.5" data-testid="welcome-banner-briefing">
                {briefing.slice(0, 6).map((b, i) => {
                  const KIcon = KIND_ICON[b.kind] || Sparkles;
                  const inner = (
                    <span className="flex items-start gap-2">
                      <KIcon
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          b.severity === "warn" ? "text-[#FFB78A]" : "text-[#ECC974]"
                        }`}
                      />
                      <span>{b.text}</span>
                    </span>
                  );
                  const href = b.checklist_id
                    ? `/checklist/${b.checklist_id}`
                    : b.boat_id
                    ? boatLinkFor(user.role, b.boat_id)
                    : null;
                  return (
                    <li
                      key={i}
                      className="text-sm text-white"
                      data-testid={`welcome-briefing-${b.kind}-${i}`}
                    >
                      {href ? (
                        <Link to={href} className="hover:text-[#ECC974] transition-colors">
                          {inner}
                        </Link>
                      ) : (
                        inner
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function roleLabelNo(role) {
  return {
    admin: "Admin",
    service: "Service",
    vask: "Renhold",
    kunde: "Kunde",
  }[role] || role;
}

function boatLinkFor(role, boatId) {
  if (role === "vask") return `/vask`;
  if (role === "service") return `/admin/boats/${boatId}`;
  return `/admin/boats/${boatId}`;
}
